import React from 'react';
import styled from 'styled-components';

interface LogoProps {
  size?: number;
  borderColor?: string;
}

const LogoContainer = styled.div<{ size: number }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
`;

const Logo: React.FC<LogoProps> = ({ 
  size = 32, 
  borderColor = '#1890ff'
}) => {
  const rocketColor = '#1890ff';
  const goldColor = '#F6C445';
  
  return (
    <LogoContainer size={size}>
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* 定义渐变和滤镜 */}
        <defs>
          <linearGradient id="rocketGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: rocketColor }} />
            <stop offset="100%" style={{ stopColor: `${rocketColor}dd` }} />
          </linearGradient>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#FFD700' }} />
            <stop offset="100%" style={{ stopColor: '#FFA500' }} />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* 白色背景圆 */}
        <circle 
          cx="50" 
          cy="50" 
          r="48" 
          fill="white"
          stroke={borderColor}
          strokeWidth="2"
        />
        
        {/* 火箭整体 */}
        <g transform="translate(50 50) rotate(-15) translate(-50 -50)">
          {/* 火箭主体 */}
          <path
            d="M50 20L30 70C30 70 50 65 70 70L50 20Z"
            fill={rocketColor}
          />
          
          {/* 火箭窗户 */}
          <circle
            cx="50"
            cy="42"
            r="5"
            fill="white"
            stroke={rocketColor}
            strokeWidth="1.5"
          />
          
          {/* 火箭翼 */}
          <path
            d="M30 70L22 62L30 70ZM70 70L78 62L70 70Z"
            stroke={rocketColor}
            strokeWidth="3"
            strokeLinecap="round"
          />
          
          {/* 金色火焰 */}
          <g filter="url(#glow)">
            <path
              d="M40 70C40 80 50 82 50 82C50 82 60 80 60 70"
              stroke="url(#goldGradient)"
              strokeWidth="4"
              strokeLinecap="round"
            >
              <animate
                attributeName="d"
                values="
                  M40 70C40 82 50 85 50 85C50 85 60 82 60 70;
                  M40 70C40 77 50 80 50 80C50 80 60 77 60 70;
                  M40 70C40 82 50 85 50 85C50 85 60 82 60 70
                "
                dur="0.5s"
                repeatCount="indefinite"
              />
            </path>
            
            {/* 金色烟雾 */}
            <path
              d="M35 80C30 85 30 90 35 90M65 80C70 85 70 90 65 90"
              stroke="url(#goldGradient)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="0 4 8 4"
            >
              <animate
                attributeName="opacity"
                values="1;0"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </g>
        </g>
        
        {/* 星星装饰 */}
        <g filter="url(#glow)">
          {[
            { cx: 20, cy: 35, delay: "0s" },
            { cx: 80, cy: 35, delay: "0.5s" },
            { cx: 30, cy: 20, delay: "1s" },
            { cx: 70, cy: 20, delay: "0.75s" }
          ].map((star, index) => (
            <circle
              key={index}
              cx={star.cx}
              cy={star.cy}
              r="2"
              fill="url(#goldGradient)"
            >
              <animate
                attributeName="opacity"
                values="0;1;0"
                dur="1.5s"
                repeatCount="indefinite"
                begin={star.delay}
              />
            </circle>
          ))}
        </g>
        
        {/* 金币装饰 */}
        <g transform="translate(50 50) rotate(-15) translate(-50 -50)" filter="url(#glow)">
          {[
            { cx: 25, cy: 60, delay: "0s" },
            { cx: 75, cy: 60, delay: "1s" }
          ].map((coin, index) => (
            <circle
              key={index}
              cx={coin.cx}
              cy={coin.cy}
              r="3"
              fill="url(#goldGradient)"
            >
              <animate
                attributeName="cy"
                values="60;55;60"
                dur="2s"
                repeatCount="indefinite"
                begin={coin.delay}
              />
            </circle>
          ))}
        </g>
      </svg>
    </LogoContainer>
  );
};

export default Logo; 